import { ReadonlyURLSearchParams } from 'next/navigation';
import { IBranch } from 'app/interfaces/branches';
import { IMapPlace } from 'app/interfaces/map';
import { encode as encodeToBase64 } from 'js-base64';

export function isServer() {
  return typeof window === 'undefined';
}

export function isOverflown({
  parent,
  element
}: {
  parent: HTMLElement;
  element: HTMLElement;
}) {
  return (
    element.scrollWidth > parent.clientWidth ||
    element.scrollHeight > parent.clientHeight
  );
}

export function resizeText({
  element,
  parent,
  min,
  max,
  step = 1,
  unit = 'px'
}: {
  min: number;
  max: number;
  element: HTMLElement;
  parent: HTMLElement;
  step?: number;
  unit?: string;
}) {
  let i = min;
  let overflow = false;
  while (!overflow && i < max) {
    element.style.fontSize = `${i}${unit}`;
    overflow = isOverflown({ parent, element });
    if (!overflow) i += step;
  }
  element.style.fontSize = `${i - step}${unit}`;
}

export function capitalizeFirstLetter(str: string) {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function pluralFormat(
  value: number,
  pluralForms: {
    singular: string;
    pluralSmall: string;
    plural: string;
  }
): string {
  const dividedByTen = Math.floor(value / 10);

  if (value % 10 === 1 && dividedByTen % 10 !== 1) {
    return `${value} ${pluralForms.singular}`;
  }

  if (value % 10 > 1 && value % 10 < 5 && dividedByTen % 10 !== 1) {
    return `${value} ${pluralForms.pluralSmall}`;
  }

  return `${value} ${pluralForms.plural}`;
}

export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  if (min === max) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function copyToClipboard(str: string) {
  if (!str) return;
  const el = document.createElement('textarea');
  el.value = str;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  el.setSelectionRange(0, 99999);
  if (
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    navigator.clipboard.writeText(str);
  } else {
    document.execCommand('copy');
  }
  document.body.removeChild(el);
}

export function getSearchParams(
  currentParams: ReadonlyURLSearchParams | URLSearchParams | null,
  newParams: { name: string; value?: string | string[] }[] = []
) {
  const oldParamsArray = currentParams
    ? Array.from(currentParams.entries())
    : [];
  const params = new URLSearchParams(oldParamsArray);
  newParams.forEach(param => {
    if (!param.value || !param.value?.length) {
      params.delete(param.name);
      return;
    }
    if (Array.isArray(param.value)) {
      params.delete(param.name);
      param.value.forEach(v => {
        params.append(param.name, v);
      });
      return;
    }
    params.set(param.name, param.value);
  });

  return params.toString();
}

export function freezeScroll() {
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
}

export function releaseScroll() {
  document.documentElement.style.removeProperty('overflow');
  document.body.style.removeProperty('overflow');
}

export function parseCoords(
  location?: string | null,
  reverse: boolean = false
): [number, number] | null {
  if (typeof location !== 'string') return null;
  try {
    const result = location
      .replace(' ', '')
      .split(',')
      .map(el => Number(el));
    if (result.length !== 2) return null;
    if (reverse) {
      return [result[1], result[0]];
    }
    return [result[0], result[1]];
  } catch (error) {
    return null;
  }
}

export function getBranchMapPlace(
  item: Pick<IBranch, 'id' | 'coordinates' | 'name'>
): IMapPlace | null {
  if (!item.coordinates) return null;

  return {
    id: item.id,
    title: item.name,
    coordinates: item.coordinates
  };
}

export function breakWords(str: string, delimiter: string = '<br>') {
  if (!str) return '';
  return str
    .split(' ')
    .filter(el => el)
    .join('<br>');
}

export function getDateFormat(
  date?: Date | string | null,
  locale: string = 'ru'
) {
  try {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return '';
  }
}

export function normalizeFilterParam(param: any | any[]) {
  if (!param) return [];
  return Array.isArray(param) ? param : [param];
}

export function cleanPhone(phone: string) {
  if (!phone) return '';
  return phone.replace(/[^0-9\+]/g, '');
}

// Форматирует цену в рублях с разделением тысяч неразрывным пробелом: 4500 → «4 500 ₽»
export function formatPrice(price: number) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(price);
}

export function getBasicAuthorizationHeader({
  login,
  password
}: {
  login: string;
  password: string;
}) {
  if (!login || !password) return '';
  const token = encodeToBase64(`${login}:${password}`);
  return `Basic ${token}`;
}
